import { useState, useEffect } from 'react';
import { Save, RefreshCw, ChevronDown, ChevronUp, Check, X, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPrestations = () => {
  const [prestations, setPrestations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('entretien');
  const [expandedCards, setExpandedCards] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [newItemText, setNewItemText] = useState('');

  const getAuthHeader = () => {
    const password = localStorage.getItem('idmat_admin');
    return {
      headers: {
        'Authorization': 'Basic ' + btoa('admin:' + password)
      }
    };
  };

  const fetchPrestations = async () => {
    try {
      const response = await axios.get(`${API}/admin/prestations`, getAuthHeader());
      setPrestations(response.data);
    } catch (error) {
      console.error('Error fetching prestations:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrestations();
  }, []);

  const toggleCard = (id) => {
    setExpandedCards(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleSavePrestation = async (prestation) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/prestations/${prestation.id}`, prestation, getAuthHeader());
      toast.success('Prestation mise à jour');
      fetchPrestations();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (prestation) => {
    const updated = { ...prestation, active: !prestation.active };
    await handleSavePrestation(updated);
  };

  const handlePriceChange = (prestationId, price) => {
    setPrestations(prev => prev.map(p => 
      p.id === prestationId ? { ...p, price } : p
    ));
  };

  const handleToggleItem = (prestationId, itemId) => {
    setPrestations(prev => prev.map(p => {
      if (p.id === prestationId) {
        return {
          ...p,
          items: p.items.map(item => 
            item.id === itemId ? { ...item, included: !item.included } : item
          )
        };
      }
      return p;
    }));
  };

  const handleAddItem = (prestationId) => {
    if (!newItemText.trim()) return;
    
    setPrestations(prev => prev.map(p => {
      if (p.id === prestationId) {
        return {
          ...p,
          items: [...p.items, { 
            id: Date.now().toString(), 
            name: newItemText.trim(), 
            included: true 
          }]
        };
      }
      return p;
    }));
    setNewItemText('');
    setEditingItem(null);
  };

  const handleDeleteItem = (prestationId, itemId) => {
    setPrestations(prev => prev.map(p => {
      if (p.id === prestationId) {
        return {
          ...p,
          items: p.items.filter(item => item.id !== itemId)
        };
      }
      return p;
    }));
  };

  const handleResetPrestations = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir réinitialiser toutes les prestations aux valeurs par défaut ?')) return;
    
    try {
      await axios.post(`${API}/admin/prestations/reset`, {}, getAuthHeader());
      toast.success('Prestations réinitialisées');
      fetchPrestations();
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const filteredPrestations = prestations.filter(p => p.category_type === activeTab);
  const equipmentOrder = ['micro_pelle', 'pelle_compacte', 'pelle_moyenne', 'tracto_pelle'];
  const sortedPrestations = [...filteredPrestations].sort((a, b) => {
    return equipmentOrder.indexOf(a.equipment_type) - equipmentOrder.indexOf(b.equipment_type);
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-[#F5C500] border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <div data-testid="admin-prestations">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <h1 
            className="text-3xl font-bold text-[#1a1a1a]"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Gestion des Prestations
          </h1>
          <button
            onClick={handleResetPrestations}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 hover:border-red-500 hover:text-red-500 transition-colors"
            data-testid="reset-btn"
          >
            <RefreshCw size={18} />
            Réinitialiser
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white shadow-sm mb-6 flex">
          <button
            onClick={() => setActiveTab('entretien')}
            className={`flex-1 px-6 py-4 font-bold uppercase tracking-wide transition-colors border-b-4 ${
              activeTab === 'entretien' 
                ? 'border-[#F5C500] text-[#1a1a1a]' 
                : 'border-transparent text-gray-500 hover:text-[#1a1a1a]'
            }`}
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            🔧 Entretien
          </button>
          <button
            onClick={() => setActiveTab('peinture')}
            className={`flex-1 px-6 py-4 font-bold uppercase tracking-wide transition-colors border-b-4 ${
              activeTab === 'peinture' 
                ? 'border-[#F5C500] text-[#1a1a1a]' 
                : 'border-transparent text-gray-500 hover:text-[#1a1a1a]'
            }`}
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            🎨 Peinture
          </button>
        </div>

        {/* Prestations List */}
        <div className="space-y-4">
          {sortedPrestations.map((prestation) => (
            <div 
              key={prestation.id} 
              className={`bg-white shadow-sm border-l-4 ${prestation.active ? 'border-[#F5C500]' : 'border-gray-300 opacity-60'}`}
              data-testid={`admin-prestation-${prestation.equipment_type}`}
            >
              {/* Header */}
              <div className="p-4 flex items-center justify-between">
                <div 
                  className="flex items-center gap-4 flex-1 cursor-pointer"
                  onClick={() => toggleCard(prestation.id)}
                >
                  <div>
                    <h3 
                      className="text-lg font-bold text-[#1a1a1a]"
                      style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    >
                      {prestation.equipment_label}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {prestation.items?.filter(i => i.included).length || 0} / {prestation.items?.length || 0} prestations actives
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  {/* Price input */}
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={prestation.price || ''}
                      onChange={(e) => handlePriceChange(prestation.id, e.target.value)}
                      placeholder="Prix (ex: 350€)"
                      className="w-32 px-3 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none text-right"
                    />
                  </div>
                  
                  {/* Active toggle */}
                  <button
                    onClick={() => handleToggleActive(prestation)}
                    className={`px-3 py-2 text-sm font-medium ${
                      prestation.active 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {prestation.active ? 'Actif' : 'Inactif'}
                  </button>
                  
                  {/* Save button */}
                  <button
                    onClick={() => handleSavePrestation(prestation)}
                    disabled={saving}
                    className="p-2 bg-[#F5C500] text-[#1a1a1a] hover:bg-[#ddb200] transition-colors disabled:opacity-50"
                    title="Enregistrer"
                  >
                    <Save size={18} />
                  </button>
                  
                  {/* Expand/Collapse */}
                  <button
                    onClick={() => toggleCard(prestation.id)}
                    className="p-2 hover:bg-gray-100"
                  >
                    {expandedCards[prestation.id] ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </button>
                </div>
              </div>

              {/* Content */}
              {expandedCards[prestation.id] && (
                <div className="px-4 pb-4 border-t border-gray-100">
                  <div className="mt-4 space-y-2">
                    {prestation.items?.map((item) => (
                      <div 
                        key={item.id} 
                        className={`flex items-center gap-3 p-3 ${item.included ? 'bg-green-50' : 'bg-gray-50'}`}
                      >
                        <button
                          onClick={() => handleToggleItem(prestation.id, item.id)}
                          className={`w-6 h-6 rounded flex items-center justify-center ${
                            item.included 
                              ? 'bg-green-500 text-white' 
                              : 'bg-gray-300 text-white'
                          }`}
                        >
                          {item.included ? <Check size={14} /> : <X size={14} />}
                        </button>
                        <span className={`flex-1 ${item.included ? 'text-gray-700' : 'text-gray-400 line-through'}`}>
                          {item.name}
                        </span>
                        <button
                          onClick={() => handleDeleteItem(prestation.id, item.id)}
                          className="p-1 text-red-500 hover:bg-red-50"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Add new item */}
                  <div className="mt-4 flex gap-2">
                    {editingItem === prestation.id ? (
                      <>
                        <input
                          type="text"
                          value={newItemText}
                          onChange={(e) => setNewItemText(e.target.value)}
                          placeholder="Nouvelle prestation..."
                          className="flex-1 px-3 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                          onKeyPress={(e) => e.key === 'Enter' && handleAddItem(prestation.id)}
                        />
                        <button
                          onClick={() => handleAddItem(prestation.id)}
                          className="px-4 py-2 bg-[#F5C500] text-[#1a1a1a] font-medium"
                        >
                          Ajouter
                        </button>
                        <button
                          onClick={() => { setEditingItem(null); setNewItemText(''); }}
                          className="px-4 py-2 border border-gray-200 hover:bg-gray-50"
                        >
                          Annuler
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setEditingItem(prestation.id)}
                        className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 text-gray-500 hover:border-[#F5C500] hover:text-[#F5C500] transition-colors w-full justify-center"
                      >
                        <Plus size={18} />
                        Ajouter une prestation
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPrestations;
